import is from '@sindresorhus/is';
import type { RefObject } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';

export type UseDragToScrollResult = {
    readonly containerProps: {
        readonly onMouseDown: (e: React.MouseEvent) => void;
        readonly ref: RefObject<HTMLDivElement | null>;
    };
    readonly isDragging: boolean;
};

const INTERACTIVE_ELEMENTS = ['INPUT', 'BUTTON', 'TEXTAREA', 'SELECT', 'A'];
const MOMENTUM_FRICTION = 0.95;
const MOMENTUM_THRESHOLD = 0.5;

const isInteractiveElement = (element: Element): boolean => {
    return INTERACTIVE_ELEMENTS.includes(element.tagName);
};

const isScrollableElement = (element: HTMLElement): boolean => {
    const style = window.getComputedStyle(element);
    const overflowX = style.overflowX;

    return (overflowX === 'auto' || overflowX === 'scroll') && element.scrollWidth > element.clientWidth;
};

const findScrollTarget = (element: HTMLElement, container: HTMLElement): HTMLElement => {
    let current: HTMLElement | null = element;

    while (is.truthy(current) && current !== container) {
        if (isScrollableElement(current)) {
            return current;
        }
        current = current.parentElement;
    }

    return container;
};

export const useDragToScroll = (): UseDragToScrollResult => {
    const containerRef = useRef<HTMLDivElement | null>(null);
    const isDraggingRef = useRef(false);
    const isSpaceHeldRef = useRef(false);
    const startXRef = useRef(0);
    const scrollLeftRef = useRef(0);
    const velocityRef = useRef(0);
    const lastXRef = useRef(0);
    const lastTimeRef = useRef(0);
    const scrollTargetRef = useRef<HTMLElement | null>(null);
    const animationFrameRef = useRef<number | null>(null);

    const [isDragging, setIsDragging] = useState(false);

    const stopMomentum = useCallback((): void => {
        if (is.number(animationFrameRef.current)) {
            cancelAnimationFrame(animationFrameRef.current);
            animationFrameRef.current = null;
        }
    }, []);

    const applyMomentum = useCallback((): void => {
        const target = scrollTargetRef.current;

        if (is.nullOrUndefined(target)) {
            return;
        }

        velocityRef.current *= MOMENTUM_FRICTION;

        const maxScroll = target.scrollWidth - target.clientWidth;
        const newScrollLeft = target.scrollLeft - velocityRef.current;
        target.scrollLeft = Math.max(0, Math.min(newScrollLeft, maxScroll));

        const isAtBoundary = target.scrollLeft <= 0 || target.scrollLeft >= maxScroll;
        const hasVelocity = Math.abs(velocityRef.current) > MOMENTUM_THRESHOLD;

        animationFrameRef.current = hasVelocity && !isAtBoundary ? requestAnimationFrame(applyMomentum) : null;
    }, []);

    const handleMouseMove = useCallback((event: MouseEvent): void => {
        if (!isDraggingRef.current) {
            return;
        }

        const target = scrollTargetRef.current;

        if (is.nullOrUndefined(target)) {
            return;
        }

        const currentTime = performance.now();
        const deltaTime = currentTime - lastTimeRef.current;
        const deltaX = event.clientX - lastXRef.current;

        if (deltaTime > 0) {
            velocityRef.current = (deltaX / deltaTime) * 16;
        }

        lastXRef.current = event.clientX;
        lastTimeRef.current = currentTime;

        const movementX = event.clientX - startXRef.current;
        const newScrollLeft = scrollLeftRef.current - movementX;
        const maxScroll = target.scrollWidth - target.clientWidth;
        target.scrollLeft = Math.max(0, Math.min(newScrollLeft, maxScroll));
    }, []);

    const handleMouseUp = useCallback((): void => {
        if (isDraggingRef.current === false) {
            return;
        }

        isDraggingRef.current = false;
        setIsDragging(false);
        document.body.style.userSelect = '';

        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);

        if (Math.abs(velocityRef.current) > MOMENTUM_THRESHOLD) {
            applyMomentum();
        }
    }, [handleMouseMove, applyMomentum]);

    const handleMouseDown = useCallback(
        (event: React.MouseEvent): void => {
            const container = containerRef.current;

            if (is.nullOrUndefined(container)) {
                return;
            }

            const target = event.target as Element;

            if (isInteractiveElement(target)) {
                return;
            }

            const isMiddleMouseButton = event.button === 1;
            const isSpaceLeftClick = isSpaceHeldRef.current && event.button === 0;

            if (!isMiddleMouseButton && !isSpaceLeftClick) {
                return;
            }

            event.preventDefault();
            stopMomentum();

            const scrollTarget = findScrollTarget(target as HTMLElement, container);

            isDraggingRef.current = true;
            setIsDragging(true);
            startXRef.current = event.clientX;
            scrollLeftRef.current = scrollTarget.scrollLeft;
            lastXRef.current = event.clientX;
            lastTimeRef.current = performance.now();
            velocityRef.current = 0;
            scrollTargetRef.current = scrollTarget;

            document.body.style.userSelect = 'none';

            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
        },
        [handleMouseMove, handleMouseUp, stopMomentum],
    );

    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent): void => {
            const target = event.target as Element;

            if (event.code === 'Space' && !isSpaceHeldRef.current && !isInteractiveElement(target)) {
                isSpaceHeldRef.current = true;
            }
        };

        const handleKeyUp = (event: KeyboardEvent): void => {
            if (event.code === 'Space') {
                isSpaceHeldRef.current = false;
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        document.addEventListener('keyup', handleKeyUp);

        return () => {
            document.removeEventListener('keydown', handleKeyDown);
            document.removeEventListener('keyup', handleKeyUp);
        };
    }, []);

    useEffect(() => {
        return () => {
            stopMomentum();
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
            document.body.style.userSelect = '';
        };
    }, [handleMouseMove, handleMouseUp, stopMomentum]);

    return {
        containerProps: {
            onMouseDown: handleMouseDown,
            ref: containerRef,
        },
        isDragging,
    };
};
